<template>
  <div class="list-detail fh fw" v-click-outside="closeAll">
    <div v-if="selectedData.loading" class="fh fw center">
      <loading-circle />
    </div>

    <div v-else-if="!selectedData.id && !selectedData.data" class="fh fw center p-5">
      <p class="text-s">Select a list to view details</p>
    </div>

    <div class="list-detail-content fh" v-else>
      <div class="form-template-bar v-center space-between px-3 py-3">
        <h1 class="weight-500 s list-title elipsis-text mr-2" v-overflow-tooltip>{{ selectedData?.data?.name }}</h1>
        <div class="v-center flex-shrink-0">
          <div class="input-group search m mx-2">
            <input type="text" v-model="searchKeyword" placeholder="Search items..." class="text-s" />
          </div>
          <div class="flex v-center custom-list-actions" v-if="isTenantAdmin">
            <button class="btn text-xs" style="height: 32px;" @click.stop="openListCopyModal">
              Copy
            </button>
            <button class="btn text-xs" style="height: 32px;" @click.stop="importCsvModal = true">
              Import CSV
            </button>
            <button
              class="btn text-xs"
              :class="{ 'opacity-50 cursor-not-allowed': isCreateButtonDisabled }"
              style="height: 32px;"
              :disabled="isCreateButtonDisabled"
              @click.stop="startTopLevelCreate"
            >
              <span class="mr-1">+</span> Add
            </button>
          </div>
        </div>
      </div>

      <div class="custom-list-container" @scroll="onScroll" style="overflow-y: auto; height: calc(100% - 64px);">
        <div v-if="searchLoading" class="px-3 py-2 text-xs color-grey">
          Searching items...
        </div>
<div v-if="addingParent" class="inline-row v-center px-3 py-2 child-add-bg">
  <div class="chevron-box"></div>

  <div class="input-container flex-grow-1 mr-3">
    <input
      v-model="newName"
      type="text"
      placeholder="New item name"
      class="modern-input active-input text-xs w-100"
      v-focus
    />
  </div>

  <div class="abbreviation-container mr-3">
    <input
      v-model="newAbbreviatedName"
      type="text"
      placeholder="Abbreviation"
      class="modern-input abbr-width text-xs"
    />
  </div>

  <div class="btn-group-right flex ml-auto">
    <button class="btn text-xs mr-1" @click="saveTopLevel">Add</button>
    <button class="btn btn-black text-xs" @click="cancelTopLevelCreate">Cancel</button>
  </div>
</div>

        <list-row
          v-for="item in filteredList"
          :key="item.id"
          :item="item"
          :parent-id="null"
          :depth="0"
          :is-tenant-admin="isTenantAdmin"
          :is-system-generated-list="isSystemGeneratedList"
          :selected-list-id="selected.id"
          :all-values="allListValues"
          :top-level-value-count="topLevelValueCount"
          :expanded-state="expandedState"
          :active-path-set="activePathSet"
          :is-searching="isSearching"
          :editing-row-id="activeMode.editingRowId"
          :is-creating="activeMode.isCreating"
          :is-rearrange-enabled="canRearrange"
          :dragged-item-id="draggedItemId"
          :dragged-parent-id="draggedParentId"
          @refresh="refreshData"
          @edit-start="handleEditStart"
          @edit-end="handleEditEnd"
          @create-start="handleCreateStart"
          @create-end="handleCreateEnd"
          @toggle-expand="handleToggleExpand"
          @children-loaded="handleChildrenLoaded"
          @drag-start="handleDragStart"
          @drag-end="handleDragEnd"
          @drop-item="handleDrop"
          @copy-item="openCopyModal"
          :fetch-children="fetchChildren"
        />
        <div v-if="loadingMoreTopLevel && !searchLoading" class="center py-2 text-xs color-grey">
          Loading...
        </div>
      </div>
    </div>
    <modal
      class="import-csv-modal"
      title="Import from CSV"
      description="Upload a CSV file and map its columns to import items"
      :open="importCsvModal"
      @close="importCsvModal = false"
    >
      <import-custom-list
        v-if="importCsvModal"
        :custom-list-id="selected.id"
        @imported="refreshData"
        @close="importCsvModal = false"
      />
    </modal>
    <copy-list-values-modal
      :open="copyModalOpen"
      :items-loading="copyCandidatesLoading"
      :source-list-name="selectedData?.data?.name || ''"
      :source-item="copySourceItem"
      :items="copyCandidates"
      :target-lists="availableTargetLists"
      :target-parent-options="targetParentOptions"
      :default-target-list-id="selected?.id"
      @close="closeCopyModal"
      @target-list-change="handleTargetListChange"
      @applied-resolutions="handleAppliedResolutions"
    />
    <custom-list-value-resolution-modal
      :open="topLevelResolutionOpen"
      :title="topLevelResolutionTitle"
      :description="topLevelResolutionDescription"
      :source-name="topLevelDraftName"
      :source-abbreviation="topLevelDraftAbbreviation"
      :name-conflict="topLevelNameConflict"
      :abbreviation-conflict="topLevelAbbreviationConflict"
      :name-deleted-conflict="topLevelDeletedNameConflict"
      :abbreviation-deleted-conflict="topLevelDeletedAbbreviationConflict"
      :show-both-fields="topLevelShowBothFields"
      :show-name-field="topLevelShowNameField"
      :show-abbreviation-field="topLevelShowAbbreviationField"
      :initial-name="topLevelDraftName"
      :initial-abbreviation="topLevelDraftAbbreviation"
      :name-error="topLevelNameError"
      :abbreviation-error="topLevelAbbreviationError"
      @draft-change="clearTopLevelResolutionErrors"
      @close="closeTopLevelResolution"
      @skip="closeTopLevelResolution"
      @save="applyTopLevelResolution"
    />
  </div>
</template>

<script>
import loadingCircle from '../../common/loadingCircle.vue'
import ListRow from './listRow.vue'
import Modal from '../../common/modal.vue'
import ImportCustomList from './importCustomList.vue'
import CopyListValuesModal from './copyListValuesModal.vue'
import CustomListValueResolutionModal from './customListValueResolutionModal.vue'
import { mapGetters } from 'vuex'
import {
  CreateCustomListValues,
  GetCustomListChild,
  GetCustomList,
  GetPaginatedCustomList,
  CheckTopLevelCustomListValueByName,
  CheckTopLevelCustomListValueByAbbreviation,
  UpdateCustomListValues,
  ChangeOrderCustomListValues,
  GetPaginatedCustomListChild
} from '@/api'
import Loader from '@/plugins/loader'
import ConfirmationDialog from '@/plugins/confirmationDialog'
import { alert } from '@/plugins/notification'

export default {
  name: 'ListDetail',
  components: { loadingCircle, ListRow, Modal, ImportCustomList, CopyListValuesModal, CustomListValueResolutionModal },
  directives: { focus: { inserted: el => el.focus() } },
  props: {
    selected: {
      type: Object,
      required: true
    },
    customLists: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      searchKeyword: '',
      addingParent: false,
      importCsvModal: false,
      displayList: [],
      topLevelItems: [],
      limit: 30,
      offset: 0,
      hasMore: true,
      loadingMoreTopLevel: false,
      searchTimeout: null,
      expandedState: {},
      childCache: {},
      searchLoading: false,
      forceReloadTree: false,
      searchRequestId: 0,
      suppressKeywordWatch: false,
      newName: '',
      newAbbreviatedName: '',
      activeMode: {
        isCreating: false,
        editingRowId: null
      },
      draggedItemId: null,
      draggedParentId: null,
      copyModalOpen: false,
      copySourceItem: null,
      copyCandidates: [],
      copyCandidatesLoading: false,
      targetParentOptions: [],
      topLevelResolutionOpen: false,
      topLevelDraftName: '',
      topLevelDraftAbbreviation: '',
      topLevelNameConflict: false,
      topLevelAbbreviationConflict: false,
      topLevelDeletedNameConflict: false,
      topLevelDeletedAbbreviationConflict: false,
      topLevelDeletedMatch: null,
      topLevelNameError: false,
      topLevelAbbreviationError: false,
      topLevelResolutionTitle: 'Resolve duplicate top-level item',
      topLevelResolutionDescription: 'Update the name or abbreviation, or skip this item.',
      topLevelShowBothFields: false,
      topLevelShowNameField: true,
      topLevelShowAbbreviationField: true
    }
  },
  computed: {
    ...mapGetters(['user']),
    isTenantAdmin () { return this.user.tenantLevelRole === 'ADMIN' },
    selectedData () { return this.selected || { id: null, loading: false, data: null } },
    isSystemGeneratedList () {
      return Boolean(this.selectedData?.data?.system_generated ?? this.selected?.system_generated)
    },
    filteredList () { return this.displayList },
    normalizedSearchKeyword () { return this.searchKeyword.trim().toLowerCase() },
    isSearching () { return Boolean(this.normalizedSearchKeyword) },
    canRearrange () { return !this.isSearching },
    isCreateButtonDisabled () {
      return this.activeMode.isCreating || this.activeMode.editingRowId !== null
    },
    availableTargetLists () {
      return Array.isArray(this.customLists) ? this.customLists : []
    },
    allListValues () {
      if (Array.isArray(this.topLevelItems) && this.topLevelItems.length) {
        return this.topLevelItems
      }
      return Array.isArray(this.selectedData?.data?.custom_list_values)
        ? this.selectedData.data.custom_list_values
        : []
    },
    topLevelValueCount () {
      const selectedList = this.availableTargetLists.find(list => String(list.id) === String(this.selectedData?.id))
      return selectedList?.custom_list_values_aggregate?.aggregate?.count ?? this.allListValues.length
    },
    activePathSet () {
      return { ...this.expandedState }
    }
  },
  watch: {
    'selectedData.id': {
      immediate: true,
      handler (newId, oldId) {
        // Suppress the keyword watcher so clearing the search here
        // does not race with the fresh load triggered below.
        this.suppressKeywordWatch = true
        this.searchKeyword = ''
        this.suppressKeywordWatch = false

        if (newId === oldId) return
        this.expandedState = {}
        this.childCache = {}
        this.displayList = []
        this.topLevelItems = []
        this.forceReloadTree = false
        this.searchLoading = false
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout)
          this.searchTimeout = null
        }
        if (newId) {
          this.loadTopLevelItems(true)
        }
      }
    },
    searchKeyword () {
      if (this.suppressKeywordWatch) return
      if (this.searchTimeout) clearTimeout(this.searchTimeout)

      // Clear stale rows immediately so the user never sees old results
      // while the debounce timer or API request is in flight.
      this.displayList = []

      if (this.normalizedSearchKeyword) {
        // Show the searching indicator right away (before debounce fires).
        this.searchLoading = true
      } else {
        // Keyword was cleared — no search in flight, reset the flag.
        this.searchLoading = false
      }

      this.searchTimeout = setTimeout(() => {
        this.loadTopLevelItems(true)
      }, 300)
    }
  },
  methods: {
    async loadTopLevelItems (reset = false) {
      if (!this.selected.id) return

      // A reset (triggered by search or list change) must always proceed;
      // only block pagination appends when a request is already in-flight.
      if (!reset && (!this.hasMore || this.loadingMoreTopLevel)) return

      if (reset) {
        this.offset = 0
        this.topLevelItems = []
        this.hasMore = true
        // Cancel any in-flight request so its response is discarded.
        this.searchRequestId += 1
      }

      // Stamp this request so we can discard stale responses that arrive
      // out of order (e.g. a slow previous search finishing after a newer one).
      const requestId = this.searchRequestId
      this.loadingMoreTopLevel = true

      try {
        const res = await GetPaginatedCustomList(this.selected.id, this.limit, this.offset, this.searchKeyword)

        // Discard if a newer request has already been issued.
        if (requestId !== this.searchRequestId) return

        const items = res.core_custom_list_by_pk?.custom_list_values || []

        if (items.length < this.limit) {
          this.hasMore = false
        }

        const filteredItems = items.filter(item => !item?.deleted)
        this.topLevelItems = reset ? filteredItems : [...this.topLevelItems, ...filteredItems]
        this.offset += this.limit

        // Guard restoreVisibleTree with the same stamp so a slow child-fetch
        // from a stale keyword can't overwrite a fresher result.
        await this.restoreVisibleTree(requestId)
      } finally {
        if (requestId === this.searchRequestId) {
          this.loadingMoreTopLevel = false
        }
      }
    },
    onScroll (e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        this.loadTopLevelItems()
      }
    },
    normalizeName (value) {
      return (value || '').trim().toLowerCase()
    },
    formatTopLevelValueLabel (name, abbreviation) {
      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      if (!trimmedAbbreviation) return trimmedName
      return `${trimmedName} [${trimmedAbbreviation}]`
    },
    async getTopLevelMatchByName (name) {
      const res = await CheckTopLevelCustomListValueByName(this.selected.id, name)
      const values = res?.custom_list_values || []
      const normalizedTargetName = this.normalizeName(name)
      return values.find(item => this.normalizeName(item.name) === normalizedTargetName) || null
    },
    async getTopLevelMatchByAbbreviation (abbreviation) {
      const res = await CheckTopLevelCustomListValueByAbbreviation(
        this.selected.id,
        abbreviation
      )
      const values = res?.custom_list_values || []
      const normalizedTarget = this.normalizeName(abbreviation)
      return (
        values.find(
          (item) => this.normalizeName(item.abbreviation) === normalizedTarget
        ) || null
      )
    },
    async getTopLevelDuplicateState (name, abbreviation = '') {
      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      const [nameMatch, abbreviationMatch] = await Promise.all([
        this.getTopLevelMatchByName(trimmedName),
        trimmedAbbreviation ? this.getTopLevelMatchByAbbreviation(trimmedAbbreviation) : Promise.resolve(null)
      ])
      const deletedNameMatch = nameMatch && nameMatch.deleted ? nameMatch : null
      const deletedAbbreviationMatch = abbreviationMatch && abbreviationMatch.deleted ? abbreviationMatch : null
      return {
        nameConflict: Boolean(nameMatch && !nameMatch.deleted),
        abbreviationConflict: Boolean(trimmedAbbreviation && abbreviationMatch && !abbreviationMatch.deleted),
        deletedNameConflict: Boolean(deletedNameMatch),
        deletedAbbreviationConflict: Boolean(trimmedAbbreviation && deletedAbbreviationMatch),
        deletedMatch: deletedNameMatch || deletedAbbreviationMatch,
        deletedMatchLabel: deletedNameMatch || deletedAbbreviationMatch
          ? this.formatTopLevelValueLabel(
            (deletedNameMatch || deletedAbbreviationMatch).name,
            (deletedNameMatch || deletedAbbreviationMatch).abbreviation
          )
          : ''
      }
    },
    openTopLevelResolution ({
      name,
      abbreviation,
      nameConflict,
      abbreviationConflict,
      deletedNameConflict = false,
      deletedAbbreviationConflict = false,
      deletedMatch = null
    }) {
      this.topLevelDraftName = name || ''
      this.topLevelDraftAbbreviation = abbreviation || ''
      this.topLevelNameConflict = Boolean(nameConflict)
      this.topLevelAbbreviationConflict = Boolean(abbreviationConflict)
      this.topLevelDeletedNameConflict = Boolean(deletedNameConflict)
      this.topLevelDeletedAbbreviationConflict = Boolean(deletedAbbreviationConflict)
      this.topLevelDeletedMatch = deletedMatch
      this.clearTopLevelResolutionErrors()
      const hasActiveConflict = Boolean(nameConflict || abbreviationConflict)
      const hasDeletedConflict = Boolean(deletedNameConflict || deletedAbbreviationConflict)
      this.topLevelResolutionTitle = hasDeletedConflict && !hasActiveConflict
        ? 'Resolve deleted top-level item'
        : 'Resolve duplicate top-level item'
      this.topLevelResolutionDescription = hasDeletedConflict && !hasActiveConflict
        ? 'This value already exists as deleted. You can save it as-is or skip this item.'
        : 'Update the name or abbreviation, or skip this item.'
      this.topLevelShowBothFields = hasDeletedConflict
      this.topLevelShowNameField = hasDeletedConflict ? true : Boolean(nameConflict)
      this.topLevelShowAbbreviationField = hasDeletedConflict ? true : Boolean(abbreviationConflict)
      this.topLevelResolutionOpen = true
    },
    closeTopLevelResolution () {
      this.topLevelResolutionOpen = false
      this.topLevelDraftName = ''
      this.topLevelDraftAbbreviation = ''
      this.topLevelNameConflict = false
      this.topLevelAbbreviationConflict = false
      this.topLevelDeletedNameConflict = false
      this.topLevelDeletedAbbreviationConflict = false
      this.topLevelDeletedMatch = null
      this.clearTopLevelResolutionErrors()
      this.topLevelResolutionTitle = 'Resolve duplicate top-level item'
      this.topLevelResolutionDescription = 'Update the name or abbreviation, or skip this item.'
      this.topLevelShowBothFields = false
      this.topLevelShowNameField = true
      this.topLevelShowAbbreviationField = true
    },
    confirmRevive (label) {
      return new Promise((resolve) => {
        ConfirmationDialog(
          `"${label}" exists. Do you want to retrieve it?`,
          (isConfirmed) => resolve(Boolean(isConfirmed)),
          'Revive',
          'Cancel',
          'Revive Item'
        )
      })
    },
    clearTopLevelResolutionErrors () {
      this.topLevelNameError = false
      this.topLevelAbbreviationError = false
    },
    async validateTopLevelResolutionSave ({ name, abbreviation }) {
      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      const initialName = (this.topLevelDraftName || '').trim()
      const initialAbbreviation = (this.topLevelDraftAbbreviation || '').trim()
      const nameChanged = trimmedName.toLowerCase() !== initialName.toLowerCase()
      const abbreviationChanged = trimmedAbbreviation.toLowerCase() !== initialAbbreviation.toLowerCase()
      const hasActiveConflict = Boolean(this.topLevelNameConflict || this.topLevelAbbreviationConflict)
      const hasDeletedConflict = Boolean(this.topLevelDeletedNameConflict || this.topLevelDeletedAbbreviationConflict)

      this.clearTopLevelResolutionErrors()

      if (!hasActiveConflict && hasDeletedConflict) {
        return Boolean(trimmedName)
      }

      if (!nameChanged && !abbreviationChanged) return false

      const [nameMatch, abbreviationMatch] = await Promise.all([
        this.getTopLevelMatchByName(trimmedName),
        trimmedAbbreviation ? this.getTopLevelMatchByAbbreviation(trimmedAbbreviation) : Promise.resolve(null)
      ])

      if (nameMatch) {
        this.topLevelNameError = true
      }

      if (abbreviationMatch) {
        this.topLevelAbbreviationError = true
      }

      if (nameMatch || abbreviationMatch) return false

      return true
    },
    async refreshData () {
      this.handleCreateEnd()
      this.handleEditEnd()
      this.forceReloadTree = true
      if (this.selected.id) {
        await this.loadTopLevelItems(true)
      }
      this.$emit('update', { id: this.selected.id, index: this.selected.index })
    },
    startTopLevelCreate () {
      if (this.isCreateButtonDisabled) return
      this.handleCreateStart()
      this.addingParent = true
    },
    cancelTopLevelCreate () {
      this.addingParent = false
      this.newName = ''
      this.newAbbreviatedName = ''
      this.handleCreateEnd()
    },
    handleCreateStart () {
      this.activeMode.isCreating = true
    },
    handleCreateEnd () {
      this.activeMode.isCreating = false
    },
    handleEditStart (rowId) {
      this.activeMode.editingRowId = rowId
    },
    handleEditEnd (rowId = null) {
      if (rowId === null || this.activeMode.editingRowId === rowId) {
        this.activeMode.editingRowId = null
      }
    },
    handleToggleExpand ({ id, expanded, pathIds = [] }) {
      if (expanded) {
        const nextExpandedState = {}
        pathIds.forEach(pathId => {
          nextExpandedState[pathId] = true
        })
        this.expandedState = nextExpandedState
      } else {
        const nextExpandedState = {}
        pathIds.slice(0, -1).forEach(pathId => {
          nextExpandedState[pathId] = true
        })
        this.expandedState = nextExpandedState
      }
    },
    handleChildrenLoaded ({ parentId, children }) {
      this.$set(this.childCache, parentId, children || [])
      this.restoreVisibleTree()
    },
    cloneTree (items = []) {
      return items.map(item => ({
        ...item,
        children: item.children ? this.cloneTree(item.children) : undefined
      }))
    },
    reorderArray (list, fromIndex, toIndex) {
      if (!Array.isArray(list)) return
      if (fromIndex < 0 || toIndex < 0) return
      if (fromIndex === toIndex) return
      const movedItem = list.splice(fromIndex, 1)[0]
      if (!movedItem) return
      list.splice(toIndex, 0, movedItem)
    },
    getSiblingGroupByParentId (items = [], parentId = null) {
      if (parentId === null) return items
      const stack = [...items]
      while (stack.length) {
        const current = stack.pop()
        if (current.id === parentId) {
          if (!Array.isArray(current.children)) {
            this.$set(current, 'children', [])
          }
          return current.children
        }
        if (Array.isArray(current.children) && current.children.length) {
          stack.push(...current.children)
        }
      }
      return null
    },
    handleDragStart ({ itemId, parentId = null }) {
      this.draggedItemId = itemId
      this.draggedParentId = parentId
    },
    handleDragEnd () {
      this.draggedItemId = null
      this.draggedParentId = null
    },
    async handleDrop ({ sourceItemId, targetItemId, parentId }) {
      if (!this.canRearrange) return
      if (!sourceItemId || !targetItemId || sourceItemId === targetItemId) return

      const siblingGroup = this.getSiblingGroupByParentId(this.displayList, parentId)
      if (!Array.isArray(siblingGroup)) return

      const sourceIndex = siblingGroup.findIndex(entry => entry.id === sourceItemId)
      const targetIndex = siblingGroup.findIndex(entry => entry.id === targetItemId)
      if (sourceIndex === -1 || targetIndex === -1) return

      const previousDisplayList = this.cloneTree(this.displayList)
      const previousSelectedValues = Array.isArray(this.selected?.data?.custom_list_values)
        ? this.cloneTree(this.selected.data.custom_list_values)
        : null
      const previousCachedChildren = parentId !== null && Array.isArray(this.childCache[parentId])
        ? this.cloneTree(this.childCache[parentId])
        : null

      this.reorderArray(siblingGroup, sourceIndex, targetIndex)
      const newPos = siblingGroup.findIndex(entry => entry.id === sourceItemId) + 1

      if (parentId === null && Array.isArray(this.selected?.data?.custom_list_values)) {
        const selectedValues = this.selected.data.custom_list_values
        const topLevelSourceIndex = selectedValues.findIndex(entry => entry.id === sourceItemId)
        const topLevelTargetIndex = selectedValues.findIndex(entry => entry.id === targetItemId)
        this.reorderArray(selectedValues, topLevelSourceIndex, topLevelTargetIndex)
      }

      if (parentId !== null && Array.isArray(this.childCache[parentId])) {
        const cachedChildren = this.childCache[parentId]
        const cacheSourceIndex = cachedChildren.findIndex(entry => entry.id === sourceItemId)
        const cacheTargetIndex = cachedChildren.findIndex(entry => entry.id === targetItemId)
        this.reorderArray(cachedChildren, cacheSourceIndex, cacheTargetIndex)
        this.$set(this.childCache, parentId, [...cachedChildren])
      }

      const isReorderSuccess = await this.sendReorderMutation({ valueId: sourceItemId, newPos })
      if (!isReorderSuccess) {
        this.displayList = previousDisplayList
        if (previousSelectedValues && Array.isArray(this.selected?.data?.custom_list_values)) {
          this.$set(this.selected.data, 'custom_list_values', previousSelectedValues)
        }
        if (parentId !== null && previousCachedChildren) {
          this.$set(this.childCache, parentId, previousCachedChildren)
        }
      }
      this.handleDragEnd()
    },
    async sendReorderMutation ({ valueId, newPos }) {
      try {
        await ChangeOrderCustomListValues(valueId, newPos)
        if (this.$notify && typeof this.$notify.success === 'function') {
          this.$notify.success('Item reordered successfully!')
        }
        return true
      } catch (err) {
        if (this.$notify && typeof this.$notify.alert === 'function') {
          alert('Something went wrong. Try again')
        }
        return false
      }
    },
    async fetchChildren (parentId, offset = 0, limit = 30) {
      const res = await GetPaginatedCustomListChild(parentId, limit, offset, this.searchKeyword)
      const children = (res.custom_list_values || []).filter(item => !item?.deleted)
      const cached = this.childCache[parentId] || []
      const newCache = offset === 0 ? children : [...cached, ...children]
      this.$set(this.childCache, parentId, newCache)
      return this.cloneTree(children)
    },
    closeCopyModal () {
      this.copyModalOpen = false
      this.copySourceItem = null
      this.copyCandidates = []
      this.copyCandidatesLoading = false
      this.targetParentOptions = []
    },
    openCopyModalWithItems ({ sourceItem = null, items = [], itemsLoading = false } = {}) {
      this.copySourceItem = sourceItem
      this.copyCandidates = Array.isArray(items) ? items : []
      this.copyCandidatesLoading = itemsLoading
      this.copyModalOpen = true
    },
    async handleTargetListChange (targetListId) {
      if (!targetListId) {
        this.targetParentOptions = []
        return
      }
      try {
        const res = await GetCustomList(targetListId)
        this.targetParentOptions = res?.core_custom_list_by_pk?.custom_list_values || []
      } catch (err) {
        this.targetParentOptions = []
      }
    },
    handleAppliedResolutions ({ targetListId }) {
      const shouldRefreshCurrentList = Number(targetListId) === Number(this.selected?.id)
      if (shouldRefreshCurrentList) this.refreshData()
    },
    async openListCopyModal () {
      if (!this.selectedData?.data) return
      this.openCopyModalWithItems({
        items: [],
        itemsLoading: true
      })
      try {
        const res = await GetCustomList(this.selected.id)
        const items = res?.core_custom_list_by_pk?.custom_list_values || []
        this.copyCandidates = items.filter(item => !item?.deleted)
      } catch (err) {
        this.copyCandidates = []
        this.$notify.alert('Unable to load items for copying.')
      } finally {
        this.copyCandidatesLoading = false
      }
    },
    async openCopyModal (item) {
      if (!item?.id) return
      this.openCopyModalWithItems({
        sourceItem: item,
        itemsLoading: true
      })
      try {
        const res = await GetCustomListChild(item.id)
        const children = (res?.custom_list_values || []).filter(child => !child?.deleted)
        this.copyCandidates = children
      } catch (err) {
        this.copyCandidates = []
        this.$notify.alert(err?.message || 'Unable to load child items. You can still copy the selected item.')
      } finally {
        this.copyCandidatesLoading = false
      }
    },
    async attachVisibleChildren (items = [], { loadAll = false, forceReload = false } = {}) {
      for (const item of items) {
        const aggregateCount = item.child_values_aggregate?.aggregate?.count || 0
        const cachedChildren = this.childCache[item.id]
        const hasCachedChildren = Array.isArray(cachedChildren) && cachedChildren.length > 0
        const shouldLoadChildren = loadAll || Boolean(this.expandedState[item.id]) || hasCachedChildren
        const hasChildren = aggregateCount > 0 || hasCachedChildren

        if (!hasChildren && !shouldLoadChildren) continue
        if (!shouldLoadChildren) continue

        const children = cachedChildren && !forceReload
          ? this.cloneTree(cachedChildren)
          : await this.fetchChildren(item.id)

        this.$set(item, 'children', children)
        await this.attachVisibleChildren(children, { loadAll, forceReload })
      }
    },
    filterTree (items = [], keyword = '') {
      if (!keyword) return items

      return items.reduce((result, item) => {
        const children = Array.isArray(item.children) ? this.filterTree(item.children, keyword) : []
        const isMatch = item.name.toLowerCase().includes(keyword) ||
  item.abbreviation?.toLowerCase().includes(keyword)
        if (isMatch || children.length) {
          result.push({
            ...item,
            children
          })
        }
        return result
      }, [])
    },
    async restoreVisibleTree (requestId = null) {
      const sourceTree = this.cloneTree(this.topLevelItems)

      if (this.isSearching) {
        // The backend already filters by keyword — display top-level results
        // directly without fetching children for every match (which was causing
        // 30+ extra queries per search with large lists).
        if (requestId !== null && requestId !== this.searchRequestId) return
        this.displayList = sourceTree
        this.searchLoading = false
        this.forceReloadTree = false
        return
      }

      // Non-search path: restore previously expanded branches from cache.
      try {
        await this.attachVisibleChildren(sourceTree, {
          loadAll: false,
          forceReload: this.forceReloadTree
        })

        // Discard if a newer request was issued while children were loading.
        if (requestId !== null && requestId !== this.searchRequestId) return

        this.displayList = sourceTree
      } finally {
        if (requestId === null || requestId === this.searchRequestId) {
          this.searchLoading = false
          this.forceReloadTree = false
        }
      }
    },
    async saveTopLevel () {
      if (!this.newName.trim()) return
      const loader = new Loader()
      try {
        const trimmedName = this.newName.trim()
        const trimmedAbbreviation = this.newAbbreviatedName?.trim()
        loader.show()
        const duplicateState = await this.getTopLevelDuplicateState(trimmedName, trimmedAbbreviation)
        loader.hide()

        if (duplicateState.nameConflict || duplicateState.abbreviationConflict || (duplicateState.deletedNameConflict !== duplicateState.deletedAbbreviationConflict)) {
          this.openTopLevelResolution({
            name: trimmedName,
            abbreviation: trimmedAbbreviation,
            nameConflict: duplicateState.nameConflict,
            abbreviationConflict: duplicateState.abbreviationConflict,
            deletedNameConflict: duplicateState.deletedNameConflict,
            deletedAbbreviationConflict: duplicateState.deletedAbbreviationConflict,
            deletedMatch: duplicateState.deletedMatch
          })
          return
        }

        if (duplicateState.deletedNameConflict && duplicateState.deletedAbbreviationConflict) {
          const shouldRevive = await this.confirmRevive(duplicateState.deletedMatchLabel)
          if (!shouldRevive) return
          loader.show()
          await UpdateCustomListValues(duplicateState.deletedMatch.id, {
            name: trimmedName,
            abbreviation: trimmedAbbreviation || null,
            deleted: false
          })
          loader.hide()
          this.$notify.success('Custom list value created successfully')
          this.refreshData()
          this.closeAll()
          return
        }

        loader.show()
        await CreateCustomListValues([{
          custom_list_id: this.selected.id,
          name: trimmedName,
          abbreviation: trimmedAbbreviation || null,
          parent_id: null
        }])
        loader.hide()

        this.$notify.success('Custom list value created successfully')
        this.refreshData()
        this.closeAll()
      } catch (err) {
        loader.hide()
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Custom list value already exists')
        } else {
          this.$notify.alert(err?.message ?? 'Failed to create item')
        }
      }
    },
    async applyTopLevelResolution ({ name, abbreviation }) {
      const loader = new Loader()
      try {
        const trimmedName = (name || '').trim()
        const trimmedAbbreviation = (abbreviation || '').trim()
        if (!trimmedName) return
        loader.show()
        const isSaveAllowed = await this.validateTopLevelResolutionSave({
          name: trimmedName,
          abbreviation: trimmedAbbreviation
        })
        if (!isSaveAllowed) {
          loader.hide()
          return
        }

        const hasActiveConflict = Boolean(this.topLevelNameConflict || this.topLevelAbbreviationConflict)
        if (this.topLevelDeletedMatch && !hasActiveConflict) {
          await UpdateCustomListValues(this.topLevelDeletedMatch.id, {
            name: trimmedName,
            abbreviation: trimmedAbbreviation || null,
            deleted: false
          })
        } else {
          await CreateCustomListValues([{
            custom_list_id: this.selected.id,
            name: trimmedName,
            abbreviation: trimmedAbbreviation || null,
            parent_id: null
          }])
        }

        loader.hide()
        this.$notify.success('Custom list value created successfully')
        this.refreshData()
        this.closeAll()
      } catch (err) {
        loader.hide()
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Custom list value already exists')
        } else {
          this.$notify.alert(err?.message ?? 'Failed to create item')
        }
      }
    },
    closeAll () {
      this.addingParent = false
      this.newName = ''; this.newAbbreviatedName = ''
      this.handleCreateEnd()
      this.handleEditEnd()
      this.draggedItemId = null
      this.draggedParentId = null
      this.closeTopLevelResolution()
    }
  }
}
</script>

<style lang="scss" scoped>
.list-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.list-detail-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.form-template-bar {
  flex-shrink: 0;
  min-width: 0;
}

.list-title {
  flex: 1;
  min-width: 0;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.custom-list-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}

.inline-row {
  border-bottom: 1px solid #f0f0f0;
  min-height: 48px;
  &.child-add-bg {
    background-color: #fffcf5; /* Cream background from mockup */
    border-top: 1px solid #fff3d1;
    border-bottom: 1px solid #fff3d1;
  }
}

.modern-input {
  border: 1px solid #dee2e6;
  padding: 4px 10px;
  border-radius: 4px;
  outline: none;
  height: 30px; /* Shared height for alignment */
  background: #fff;

  &.active-input {
    border: 1px solid #d4a373; /* Golden border from mockup focus */
    box-shadow: 0 0 0 1px #d4a373;
  }
  &.abbr-width { width: 90px; }
}

.chevron-box {
  width: 24px; /* Matches row chevron wrapper for perfect horizontal alignment */
  flex-shrink: 0;
  margin-right: 12px;
}

.flex-grow-1 { flex: 1; }
.w-100 { width: 100%; }
.ml-auto { margin-left: auto; }
.mr-3 { margin-right: 0.75rem; }
.custom-list-actions { gap: 6px; }
.text-xs { font-size: var(--text-s); }
.color-grey { color: #999; }

::v-deep .import-csv-modal .modal-header {
  align-items: flex-start;
}

::v-deep .import-csv-modal .modal-title .main-title {
  font-size: var(--text-base);
  font-weight: 600;
  color: #111827;
  padding-right: 0;
}

::v-deep .import-csv-modal .modal-description {
  font-size: var(--text-xs1);
  color: #be7b18;
  line-height: 1.35;
}
</style>
